import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Book, Home, Newspaper, Loader2 } from "lucide-react";
import api from "@/lib/api";

const STR = {
  fr: {
    title: "Le blog Harf",
    subtitle: "Arabe, méthode d'apprentissage, culture. Des articles pour aller plus loin.",
    home: "Accueil",
    dictionary: "Dictionnaire",
    blog: "Blog",
    start: "Commencer",
    loading: "Chargement…",
    empty: "Aucun article publié pour l'instant.",
    error: "Le blog n'a pas pu être chargé.",
    readMore: "Lire l'article",
    ctaTitle: "Apprends l'arabe pour de bon",
    ctaBody: "Un programme quotidien, une vraie progression, un dictionnaire complet.",
    ctaButton: "Essayer gratuitement",
  },
  en: {
    title: "The Harf blog",
    subtitle: "Arabic, learning methods, culture. Articles to go further.",
    home: "Home",
    dictionary: "Dictionary",
    blog: "Blog",
    start: "Get started",
    loading: "Loading…",
    empty: "No articles published yet.",
    error: "The blog couldn't be loaded.",
    readMore: "Read the article",
    ctaTitle: "Learn Arabic for real",
    ctaBody: "A daily program, real progress, a complete dictionary.",
    ctaButton: "Try for free",
  },
};

function PublicNav({ lang, t }) {
  const nav = useNavigate();
  const homeHref = lang === "en" ? "/" : "/fr";
  const authHref = lang === "en" ? "/auth" : "/fr/auth";
  const dictHref = lang === "en" ? "/dictionary" : "/fr/dictionary";
  const blogHref = lang === "en" ? "/blog" : "/fr/blog";
  return (
    <header className="sticky top-0 z-40 pt-4 pb-3 bg-[#070d14]/90 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="max-w-4xl mx-auto px-4 flex items-center gap-2">
        <Link to={homeHref} className="flex items-center gap-2.5 shrink-0 mr-2">
          <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center font-head font-bold arabic">
            ع
          </div>
          <span className="hidden sm:block font-head font-extrabold text-lg">Harf</span>
        </Link>
        <nav className="flex items-center gap-1 rounded-full border border-white/12 bg-white/[0.05] px-1.5 py-1.5">
          <Link
            to={homeHref}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <Home size={14} /> <span className="hidden sm:inline">{t.home}</span>
          </Link>
          <Link
            to={dictHref}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <Book size={14} /> {t.dictionary}
          </Link>
          <Link
            to={blogHref}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white bg-white/10 transition"
          >
            <Newspaper size={14} /> {t.blog}
          </Link>
        </nav>
        <div className="flex-1" />
        <button
          onClick={() => nav(authHref)}
          className="px-5 py-2.5 rounded-full bg-white/12 border border-white/20 backdrop-blur-xl text-sm font-semibold hover:bg-white/20 transition shrink-0"
        >
          {t.start}
        </button>
      </div>
    </header>
  );
}

export default function Blog({ lang = "fr" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);
  const page = parseInt(params.get("page") || "1", 10);
  const t = STR[lang] || STR.fr;

  useEffect(() => {
    document.title =
      lang === "en"
        ? "The Harf Blog — Learn Arabic, Method & Culture"
        : "Le blog Harf — Apprendre l'arabe, méthode et culture";
    const setMeta = (attr, key, content) => {
      let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta(
      "name",
      "description",
      lang === "en"
        ? "Articles on learning Arabic, Modern Standard Arabic vs dialects, memorization methods, and Arabic-speaking culture."
        : "Des articles sur l'apprentissage de l'arabe, la différence entre arabe littéral et dialectes, les méthodes de mémorisation et la culture arabophone.",
    );
    const path = lang === "en" ? "/blog" : "/fr/blog";
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.origin + path);
  }, [lang]);

  useEffect(() => {
    setPosts(null);
    setError(false);
    api
      .get(`/blog/posts?lang=${lang}&page=${page}&page_size=12`)
      .then((r) => setPosts(r.data.items || []))
      .catch(() => {
        setPosts([]);
        setError(true);
      });
  }, [lang, page]);

  const wordHref = (slug) => (lang === "en" ? `/blog/${slug}` : `/fr/blog/${slug}`);

  return (
    <div className="min-h-screen bg-[#070d14] text-white">
      {!user && <PublicNav lang={lang} t={t} />}

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="font-head text-3xl sm:text-5xl font-light mb-4">
            {lang === "en" ? (
              <>
                The Harf <span className="font-bold">blog</span>
              </>
            ) : (
              <>
                Le blog <span className="font-bold">Harf</span>
              </>
            )}
          </h1>
          <p className="text-white/55 max-w-xl mx-auto">{t.subtitle}</p>
        </div>

        {posts === null && (
          <div className="flex items-center justify-center gap-2 text-white/40 text-sm py-16">
            <Loader2 size={16} className="animate-spin" /> {t.loading}
          </div>
        )}

        {posts !== null && error && (
          <div className="rounded-3xl border border-red-500/25 bg-red-500/[0.06] p-6 text-center text-sm text-white/60 max-w-md mx-auto">
            {t.error}
          </div>
        )}

        {posts !== null && !error && posts.length === 0 && (
          <div className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-10 text-center text-white/50 text-sm">
            {t.empty}
          </div>
        )}

        {posts !== null && posts.length > 0 && (
          <div className="space-y-4">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={wordHref(p.slug)}
                className="block rounded-3xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.05] hover:border-white/15 transition p-7"
              >
                <h2 className="font-head text-xl font-bold mb-2">{p.title}</h2>
                {p.excerpt && (
                  <p className="text-white/55 text-[15px] leading-relaxed mb-3">{p.excerpt}</p>
                )}
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300">
                  {t.readMore} <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16 rounded-[2rem] border border-white/[0.1] bg-white/[0.02] p-10 text-center">
          <h2 className="font-head text-2xl font-bold mb-3">{t.ctaTitle}</h2>
          <p className="text-white/55 max-w-lg mx-auto mb-7">{t.ctaBody}</p>
          <button
            onClick={() => nav(authHref)}
            className="px-7 py-3.5 rounded-full bg-white text-[#070d14] font-bold hover:bg-white/90 transition inline-flex items-center gap-2"
          >
            {t.ctaButton} <ArrowRight size={17} />
          </button>
        </div>
      </main>
    </div>
  );
}